import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Users, Swords, FileSpreadsheet, FileText, BarChart3, AlertTriangle, Shield, FlaskConical, TrendingUp, Activity, UserX, Heart, ChevronDown, ChevronRight, Globe, Loader2, History, Send, RefreshCw } from "lucide-react";
import { CLASS_COLORS } from "@/lib/constants";
import { toast } from "sonner";
import { togglePug, fetchWclParses, getWclStatus, sendRaidChangelogDiscord, refreshRaidFromWcl } from "@/lib/api";

const TANK_CLASSES = new Set(["Warrior", "Paladin", "Druid"]);

function getRaidRole(prd, player) {
  const dps = prd.dps || 0;
  const hps = prd.hps || 0;
  // Healers: HPS significantly higher than DPS
  if (hps > dps && hps > 50) return "Healer";
  // Tanks: low DPS + tank-capable class (Warriors, Paladins, Druids with < 500 dps are likely tanks)
  // We also check if they have tanking consumables/stats
  if (TANK_CLASSES.has(player.wow_class) && dps > 0 && dps < 500 && hps < dps) return "Tank";
  if (dps > 0) return "DPS";
  return "DPS";
}

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SOURCE_CONFIG = {
  rpb: { icon: FileSpreadsheet, label: "RPB Excel", color: "text-blue-400" },
  combat_log: { icon: FileText, label: "Combat Log", color: "text-orange-400" },
  data_analysis: { icon: BarChart3, label: "Data Analysis", color: "text-green-400" },
};
const TEAM_COLORS = { Monday: "#3b82f6", Thursday: "#f59e0b" };

export default function RaidDetail() {
  const { raidId } = useParams();
  const navigate = useNavigate();
  const [raid, setRaid] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unknownCollapsed, setUnknownCollapsed] = useState(true);
  const [wclCodes, setWclCodes] = useState(["", ""]);
  const [wclLinked, setWclLinked] = useState(false);
  const [wclLoading, setWclLoading] = useState(false);
  const [showWclInput, setShowWclInput] = useState(false);
  const [changelogWebhook, setChangelogWebhook] = useState(() => localStorage.getItem("discord_webhook_url") || "");
  const [changelogSending, setChangelogSending] = useState(false);
  const [showChangelogDiscord, setShowChangelogDiscord] = useState(false);
  const [refreshingWcl, setRefreshingWcl] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Fetch raid info
        const raidRes = await fetch(`${API_BASE}/api/raids/${raidId}`);
        if (!raidRes.ok) throw new Error("Raid not found");
        const raidData = await raidRes.json();
        setRaid(raidData);

        // Fetch player data for this raid
        const prdRes = await fetch(`${API_BASE}/api/player-data?raid_id=${raidId}`);
        const prdData = await prdRes.json();

        // Fetch all players to get names/classes
        const team = raidData.team || "Monday";
        const playersRes = await fetch(`${API_BASE}/api/players?team=${team}`);
        const allPlayers = await playersRes.json();
        const playerMap = {};
        allPlayers.forEach((p) => { playerMap[p.id] = p; });

        // Enrich player data
        const enriched = prdData.map((prd) => {
          const player = playerMap[prd.player_id] || { name: "Unknown", wow_class: "Unknown" };
          const gear = prd.gear || [];
          const equipped = gear.filter((g) => g.item_id > 0);
          const enchantable = equipped.filter((g) => ["Head","Shoulders","Back","Chest","Wrists","Hands","Legs","Feet","Main Hand","Off Hand"].includes(g.slot));
          const enchanted = enchantable.filter((g) => g.has_enchant).length;
          const totalGems = equipped.reduce((s, g) => s + (g.gem_count || 0), 0);
          const filledGems = equipped.reduce((s, g) => s + (g.gems_filled || 0), 0);
          const ca = prd.consumable_analysis || {};

          return {
            ...prd,
            player_name: player.name,
            wow_class: player.wow_class,
            player_id: prd.player_id,
            has_gear: equipped.length > 0,
            enchant_pct: enchantable.length > 0 ? Math.round(enchanted / enchantable.length * 100) : null,
            gem_pct: totalGems > 0 ? Math.round(filledGems / totalGems * 100) : null,
            consumable_score: ca.total_average != null ? Math.round(ca.total_average * 100) : null,
            has_consumable_analysis: !!ca.total_average,
            has_drums: !!(prd.drums_data && prd.drums_data.total_drums > 0),
            has_gear_issues: (prd.gear_issues_analysis || []).some(issue => {
              const l = issue.toLowerCase();
              return l.includes("[no enchant]") || l.includes("[no gem]") ||
                     l.includes("[common gem]") || l.includes("[meta gem inactive");
            }),
            hps: prd.hps || 0,
            is_pug: prd.is_pug || false,
            raid_role: getRaidRole(prd, player),
          };
        }).sort((a, b) => {
          // Sort by role: Tank → Healer → DPS, then alphabetically
          const roleOrder = { Tank: 0, Healer: 1, DPS: 2 };
          const ra = roleOrder[a.raid_role] ?? 3;
          const rb = roleOrder[b.raid_role] ?? 3;
          if (ra !== rb) return ra - rb;
          return a.player_name.localeCompare(b.player_name);
        });

        setPlayers(enriched);

        // Check WCL link status
        try {
          const wclStatus = await getWclStatus(raidId);
          if (wclStatus.linked) {
            setWclLinked(true);
            const codes = wclStatus.report_code ? wclStatus.report_code.split(", ") : [""];
            setWclCodes(codes.length >= 2 ? codes : [...codes, ""]);
          }
        } catch (e) { console.warn("WCL status check failed:", e); }

      } catch (e) {
        console.error(e);
        toast.error("Failed to load raid data");
      }
      setLoading(false);
    };
    load();
  }, [raidId]);

  if (loading) {
    return (
      <div className="p-12 text-center" data-testid="raid-detail-loading">
        <div className="h-8 w-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (!raid) {
    return (
      <div className="p-12 text-center text-muted-foreground">
        <p>Raid not found</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate("/calendar")}>
          Back to Calendar
        </Button>
      </div>
    );
  }

  // Determine data sources
  const sources = [];
  const srcStr = raid.source || "";
  if (srcStr.includes("rpb") || srcStr === "unified") sources.push("rpb");
  if (srcStr.includes("combat_log")) sources.push("combat_log");
  if (srcStr.includes("data_analysis") || raid.has_data_analysis) sources.push("data_analysis");
  if (sources.length === 0) sources.push("rpb");

  // Stats
  const withGear = players.filter((p) => p.has_gear).length;
  const withCA = players.filter((p) => p.has_consumable_analysis).length;
  const withDPS = players.filter((p) => p.dps > 0).length;
  const withHPS = players.filter((p) => p.hps > 0).length;
  const avgEnchant = (() => {
    const vals = players.filter((p) => p.enchant_pct !== null).map((p) => p.enchant_pct);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  })();
  const avgConsume = (() => {
    const vals = players.filter((p) => p.consumable_score !== null).map((p) => p.consumable_score);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  })();
  const issueCount = players.filter((p) => p.has_gear_issues || p.enchant_pct < 100 || p.gem_pct < 100).length;
  const pugCount = players.filter((p) => p.is_pug).length;

  const handleTogglePug = async (e, player) => {
    e.stopPropagation();
    const newVal = !player.is_pug;
    try {
      await togglePug(player.player_id, raidId, newVal);
      setPlayers((prev) => prev.map((p) =>
        p.player_id === player.player_id ? { ...p, is_pug: newVal } : p
      ));
      toast.success(`${player.player_name} marked as ${newVal ? "PUG" : "Guild"}`);
    } catch {
      toast.error("Failed to update pug status");
    }
  };

  const handleFetchParses = async () => {
    const codes = wclCodes.map(c => c.trim()).filter(Boolean);
    if (codes.length === 0) {
      toast.error("Enter at least one WarcraftLogs report code");
      return;
    }

    setWclLoading(true);
    try {
      const result = await fetchWclParses(raidId, codes);
      if (result.status === "no_data") {
        toast.warning(result.message);
      } else {
        const titles = result.report_titles?.join(", ") || "";
        toast.success(`Updated ${result.updated}/${result.total_in_raid} players from ${codes.length} report(s)`);
        if (result.unmatched_wcl_players?.length > 0) {
          toast.info(`Unmatched WCL players: ${result.unmatched_wcl_players.join(", ")}`);
        }
        setWclLinked(true);
        setShowWclInput(false);
        window.location.reload();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to fetch parses";
      toast.error(msg);
    }
    setWclLoading(false);
  };

  const handleSendRaidChangelog = async () => {
    if (!changelogWebhook.trim()) {
      toast.error("Enter a Discord webhook URL");
      return;
    }
    localStorage.setItem("discord_webhook_url", changelogWebhook.trim());
    setChangelogSending(true);
    try {
      const result = await sendRaidChangelogDiscord(changelogWebhook.trim(), raidId);
      toast.success(`Gear updates sent to Discord! (${result.total_changes} changes)`);
      setShowChangelogDiscord(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
    setChangelogSending(false);
  };

  const handleRefreshWcl = async () => {
    if (!window.confirm("Re-pull this raid from WarcraftLogs? This overwrites all stored gear and consumable data with the latest WCL pull. (DPS/HPS/parses are preserved.)")) return;
    setRefreshingWcl(true);
    try {
      const res = await refreshRaidFromWcl(raidId);
      const total = (res.summary || []).reduce((s, x) => s + (x.players_imported || 0), 0);
      toast.success(`Re-fetched ${res.summary?.length || 0} report(s) — ${total} player(s) refreshed. Reloading…`);
      // Reload the page to show the fresh gear
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
    setRefreshingWcl(false);
  };


  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="raid-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate("/calendar")}
          className="mt-1 h-8 w-8 flex-shrink-0" data-testid="back-to-calendar">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="heading text-3xl md:text-4xl font-bold tracking-tight uppercase"
            style={{ color: TEAM_COLORS[raid.team] || "#ccc" }}>
            {raid.raid_name}
          </h1>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-sm text-muted-foreground font-mono">{raid.date}</span>
            <Badge variant="outline" className="text-xs" style={{ borderColor: TEAM_COLORS[raid.team], color: TEAM_COLORS[raid.team] }}>
              {raid.team}
            </Badge>
            {raid.boss_name && (
              <span className="text-sm text-muted-foreground">{raid.boss_name}</span>
            )}
            <div className="flex gap-1.5">
              {sources.map((s) => {
                const cfg = SOURCE_CONFIG[s];
                const Icon = cfg?.icon || FileSpreadsheet;
                return (
                  <Badge key={s} variant="outline" className={`text-[10px] gap-1 ${cfg?.color || ""}`}>
                    <Icon className="h-2.5 w-2.5" /> {cfg?.label || s}
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
        <StatCard icon={Users} label="Players" value={players.length} />
        <StatCard icon={Shield} label="With Gear" value={withGear} />
        <StatCard icon={Swords} label="With DPS" value={withDPS} />
        {withHPS > 0 && <StatCard icon={Activity} label="With HPS" value={withHPS} />}
        <StatCard icon={FlaskConical} label="With Consumables" value={withCA} />
        {avgEnchant !== null && <StatCard icon={Shield} label="Avg Enchant" value={`${avgEnchant}%`} color={avgEnchant >= 90 ? "text-green-500" : avgEnchant >= 70 ? "text-yellow-500" : "text-destructive"} />}
        {avgConsume !== null && <StatCard icon={FlaskConical} label="Avg Consumable" value={`${avgConsume}%`} color={avgConsume >= 80 ? "text-green-500" : avgConsume >= 50 ? "text-yellow-500" : "text-destructive"} />}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <Button data-testid="go-negative-overview" variant="outline" size="sm"
          onClick={() => navigate(`/negative-overview?raid=${raidId}`)}
          className="gap-1.5 text-xs rounded-sm">
          <AlertTriangle className="h-3.5 w-3.5" /> Negative Overview
        </Button>
        <Button data-testid="go-team-battle" variant="outline" size="sm"
          onClick={() => navigate("/team-battle")}
          className="gap-1.5 text-xs rounded-sm">
          <Swords className="h-3.5 w-3.5" /> Team Battle
        </Button>
        <Button data-testid="go-mvp" variant="outline" size="sm"
          onClick={() => navigate("/mvp")}
          className="gap-1.5 text-xs rounded-sm">
          <TrendingUp className="h-3.5 w-3.5" /> MVP of the Week
        </Button>
        <Button data-testid="wcl-fetch-btn" variant="outline" size="sm"
          onClick={() => setShowWclInput(!showWclInput)}
          className={`gap-1.5 text-xs rounded-sm ${wclLinked ? "text-purple-400 border-purple-400/30" : ""}`}>
          <Globe className="h-3.5 w-3.5" />
          {wclLinked ? "WCL Linked" : "Fetch Parses"}
        </Button>
        <Button data-testid="send-gear-updates-btn" variant="outline" size="sm"
          onClick={() => setShowChangelogDiscord(!showChangelogDiscord)}
          className="gap-1.5 text-xs rounded-sm">
          <History className="h-3.5 w-3.5" /> Gear Updates
        </Button>
        {(raid.wcl_report_code || (raid.wcl_report_codes || []).length > 0) && (
          <Button data-testid="refresh-wcl-btn" variant="outline" size="sm"
            onClick={handleRefreshWcl}
            disabled={refreshingWcl}
            className="gap-1.5 text-xs rounded-sm text-purple-400 border-purple-400/30 hover:bg-purple-500/10"
            title="Re-pull this raid from WarcraftLogs and rebuild gear + consumables. Use this if items appear in the wrong slots.">
            {refreshingWcl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Re-fetch from WCL
          </Button>
        )}
      </div>

      {/* Bulk Gear Changelog Discord */}
      {showChangelogDiscord && (
        <div className="mb-8 p-4 rounded-sm border border-[#5865F2]/20 bg-[#5865F2]/5" data-testid="changelog-discord-panel">
          <div className="flex items-center gap-2 mb-2">
            <Send className="h-4 w-4 text-[#5865F2]" />
            <span className="text-sm font-medium text-[#5865F2]">Send Gear Updates to Discord</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Sends all gear/enchant/gem changes detected in this raid compared to the previous raid.
          </p>
          <div className="flex gap-2">
            <input
              data-testid="changelog-discord-input"
              type="text"
              value={changelogWebhook}
              onChange={(e) => setChangelogWebhook(e.target.value)}
              placeholder="Discord webhook URL..."
              className="flex-1 h-8 px-3 text-sm bg-background/50 border border-border/30 rounded-sm focus:border-[#5865F2]/50 focus:outline-none placeholder:text-muted-foreground/50"
              onKeyDown={(e) => e.key === "Enter" && handleSendRaidChangelog()}
            />
            <Button
              data-testid="changelog-discord-send"
              size="sm"
              onClick={handleSendRaidChangelog}
              disabled={changelogSending}
              className="gap-1.5 text-xs bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-sm">
              {changelogSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {changelogSending ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      )}

      {/* WCL Report Code Input */}
      {showWclInput && (
        <div className="mb-8 p-4 rounded-sm border border-purple-500/20 bg-purple-950/10" data-testid="wcl-input-panel">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="h-4 w-4 text-purple-400" />
            <span className="text-sm font-medium text-purple-300">WarcraftLogs Reports</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Paste WarcraftLogs report URLs or codes. Add one per raid instance (e.g. one for Mag, one for Gruul).
          </p>
          <div className="space-y-2">
            {wclCodes.map((code, i) => (
              <div key={i} className="flex gap-2 items-center">
                <span className="text-[10px] font-mono text-muted-foreground w-4">{i + 1}.</span>
                <input
                  data-testid={`wcl-report-input-${i}`}
                  type="text"
                  value={code}
                  onChange={(e) => {
                    const next = [...wclCodes];
                    next[i] = e.target.value;
                    setWclCodes(next);
                  }}
                  placeholder={`Report code or URL #${i + 1}...`}
                  className="flex-1 h-8 px-3 text-sm bg-background/50 border border-border/30 rounded-sm focus:border-purple-500/50 focus:outline-none placeholder:text-muted-foreground/50"
                />
              </div>
            ))}
            <button onClick={() => setWclCodes([...wclCodes, ""])}
              className="text-[10px] text-purple-400 hover:text-purple-300 transition-colors">
              + Add another report
            </button>
          </div>
          <div className="flex justify-end mt-3">
            <Button
              data-testid="wcl-fetch-submit"
              size="sm"
              onClick={handleFetchParses}
              disabled={wclLoading}
              className="gap-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-sm">
              {wclLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
              {wclLoading ? "Fetching..." : "Fetch Parses"}
            </Button>
          </div>
          {wclLinked && (
            <p className="text-[10px] text-purple-400/60 mt-2">
              Currently linked — re-fetch to update parse data
            </p>
          )}
        </div>
      )}

      {/* Player Roster for this Raid */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="heading text-xl font-bold uppercase tracking-tight">
          Raid Roster ({players.length})
        </h2>
        {pugCount > 0 && (
          <Badge variant="outline" className="text-[10px] text-orange-400 border-orange-400/30">
            <UserX className="h-3 w-3 mr-1" /> {pugCount} PUG{pugCount !== 1 ? "S" : ""}
          </Badge>
        )}
      </div>

      {issueCount > 0 && (
        <div className="mb-4 px-3 py-2 rounded-sm bg-destructive/5 border border-destructive/10 text-xs text-destructive flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" />
          {issueCount} player(s) with gear issues, missing enchants, or missing gems
        </div>
      )}

      <div className="space-y-4">
        {["Tank", "Healer", "DPS"].map((role) => {
          const rolePlayers = players.filter((p) => p.raid_role === role && p.wow_class !== "Unknown");
          if (rolePlayers.length === 0) return null;
          const RoleIcon = role === "Tank" ? Shield : role === "Healer" ? Heart : Swords;
          const roleColor = role === "Tank" ? "#C79C6E" : role === "Healer" ? "#1eff00" : "#ff4444";
          return (
            <div key={role}>
              <div className="flex items-center gap-1.5 mb-2" data-testid={`roster-role-${role.toLowerCase()}`}>
                <RoleIcon className="h-3.5 w-3.5" style={{ color: roleColor }} />
                <span className="text-xs font-mono uppercase tracking-widest" style={{ color: roleColor }}>
                  {role} ({rolePlayers.length})
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {rolePlayers.map((p) => (
                  <PlayerCard key={p.player_id} p={p} navigate={navigate} handleTogglePug={handleTogglePug} />
                ))}
              </div>
            </div>
          );
        })}

        {/* Unknown class players — collapsible */}
        {(() => {
          const unknownPlayers = players.filter((p) => p.wow_class === "Unknown");
          if (unknownPlayers.length === 0) return null;
          return (
            <div data-testid="roster-role-unknown">
              <button
                onClick={() => setUnknownCollapsed(!unknownCollapsed)}
                className="flex items-center gap-1.5 mb-2 group cursor-pointer"
                data-testid="unknown-collapse-toggle"
              >
                {unknownCollapsed
                  ? <ChevronRight className="h-3.5 w-3.5 text-yellow-500/70 transition-transform" />
                  : <ChevronDown className="h-3.5 w-3.5 text-yellow-500/70 transition-transform" />
                }
                <span className="text-xs font-mono uppercase tracking-widest text-yellow-500/70 group-hover:text-yellow-400 transition-colors">
                  Unknown ({unknownPlayers.length})
                </span>
              </button>
              {!unknownCollapsed && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {unknownPlayers.map((p) => (
                    <PlayerCard key={p.player_id} p={p} navigate={navigate} handleTogglePug={handleTogglePug} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {players.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No player data for this raid</p>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="stat-card">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className={`text-lg font-bold font-mono ${color || ""}`}>{value}</p>
    </div>
  );
}


function PlayerCard({ p, navigate, handleTogglePug }) {
  return (
    <div data-testid={`raid-player-${p.player_name}`}
      className={`flex items-center gap-3 p-3 rounded-sm border transition-all group ${p.is_pug ? "bg-orange-950/10 border-orange-500/20 hover:border-orange-500/40" : "bg-card/30 border-border/10 hover:border-border/40 hover:bg-card/50"}`}>
      <button onClick={() => navigate(`/player/${p.player_id}`)}
        className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: CLASS_COLORS[p.wow_class] || CLASS_COLORS.Unknown }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium truncate"
              style={{ color: CLASS_COLORS[p.wow_class] || CLASS_COLORS.Unknown }}>
              {p.player_name}
            </p>
            {p.is_pug && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 uppercase tracking-wider flex-shrink-0">PUG</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {p.wow_class !== "Unknown" ? (
              <span className="text-[10px] text-muted-foreground">{p.wow_class}</span>
            ) : (
              <span className="text-[10px] text-yellow-500">No class</span>
            )}
          </div>
        </div>
      </button>
      <div className="flex items-center gap-2 flex-shrink-0">
        {p.parse_pct != null && (
          <span className={`text-[10px] font-mono font-bold ${p.parse_pct >= 95 ? "text-orange-400" : p.parse_pct >= 75 ? "text-purple-400" : p.parse_pct >= 50 ? "text-blue-400" : p.parse_pct >= 25 ? "text-green-400" : "text-muted-foreground"}`}
            data-testid={`parse-badge-${p.player_name}`}>
            {p.parse_pct}%
          </span>
        )}
        {p.dps > 0 && (
          <span className="text-[10px] font-mono text-muted-foreground">
            {Math.round(p.dps).toLocaleString()} DPS
          </span>
        )}
        {p.hps > 0 && (
          <span className="text-[10px] font-mono text-green-400/70">
            {Math.round(p.hps).toLocaleString()} HPS
          </span>
        )}
        {p.consumable_score !== null && (
          <Badge variant="outline" className={`text-[10px] ${p.consumable_score >= 80 ? "text-green-500 border-green-500/30" : p.consumable_score >= 50 ? "text-yellow-500 border-yellow-500/30" : "text-destructive border-destructive/30"}`}>
            {p.consumable_score}%
          </Badge>
        )}
        {p.has_gear_issues && (
          <AlertTriangle className="h-3 w-3 text-destructive" />
        )}
        <button data-testid={`toggle-pug-${p.player_name}`}
          onClick={(e) => handleTogglePug(e, p)}
          title={p.is_pug ? "Mark as Guild" : "Mark as PUG"}
          className={`h-6 px-1.5 rounded text-[9px] font-bold uppercase tracking-wider transition-all opacity-0 group-hover:opacity-100 ${p.is_pug ? "bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" : "bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-orange-400"}`}>
          <UserX className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
